import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { DashboardLayout } from "@/components/DashboardLayout";
import { adminNav } from "@/config/nav";
import { StatusBadge } from "@/components/ui-bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Sparkles, Plus, Trash2, Loader2, Send, ArrowLeft, Check, Database, Wand2, X, CheckCircle2, Users,
} from "lucide-react";

const QTYPES = [
  { v: "mcq", l: "MCQ (single)" },
  { v: "msq", l: "MSQ (multi)" },
  { v: "short", l: "Short Answer" },
  { v: "long", l: "Long Answer" },
  { v: "descriptive", l: "Descriptive" },
];
const DIFFS = ["Easy", "Medium", "Hard"];
const TYPE_LABEL = { mcq: "MCQ", msq: "MSQ", short: "Short", long: "Long", descriptive: "Descriptive" };

function QuestionCard({ q, idx, onDelete, added }) {
  return (
    <div className={`border border-border bg-card p-4 ${added ? "opacity-60" : ""}`} data-testid={`question-${q.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2 text-xs flex-wrap">
          <span className="font-mono text-muted-foreground">Q{idx + 1}</span>
          <span className="px-1.5 py-0.5 bg-accent text-accent-foreground uppercase tracking-wide">{q.type}</span>
          <span className="px-1.5 py-0.5 bg-secondary uppercase tracking-wide">{q.difficulty}</span>
          <span className="font-mono text-muted-foreground">{q.marks} marks</span>
          {q.ai_generated && <Sparkles className="h-3.5 w-3.5 text-primary" />}
          {added && <span className="flex items-center gap-1 text-emerald-600"><CheckCircle2 className="h-3.5 w-3.5" /> In exam</span>}
        </div>
        {onDelete && <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => onDelete(q.id)} data-testid={`delete-q-${q.id}`}><Trash2 className="h-4 w-4" /></Button>}
      </div>
      <p className="mt-2 text-sm font-medium">{q.text}</p>
      {q.options?.length > 0 && (
        <ul className="mt-2 space-y-1">
          {q.options.map((o, i) => (
            <li key={i} className={`text-sm flex items-center gap-2 ${q.correct_answers?.includes(i) ? "text-emerald-700 font-medium" : "text-muted-foreground"}`}>
              {q.correct_answers?.includes(i) ? <Check className="h-3.5 w-3.5" /> : <span className="h-3.5 w-3.5 inline-block" />}
              {o}
            </li>
          ))}
        </ul>
      )}
      {q.expected_answer && <p className="mt-2 text-xs text-muted-foreground"><span className="font-medium">Model answer:</span> {q.expected_answer}</p>}
    </div>
  );
}

export default function ExamBuilder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [exam, setExam] = useState(null);

  const [gen, setGen] = useState({ topic: "", subtopic: "", options_count: 4, default_marks: 1 });
  const [spec, setSpec] = useState([{ qtype: "mcq", difficulty: "Easy", count: "5" }]);
  const [generating, setGenerating] = useState(false);
  const [preview, setPreview] = useState([]);
  const [bank, setBank] = useState([]);
  const [selectedBank, setSelectedBank] = useState({});

  // audience
  const [students, setStudents] = useState([]);
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignMode, setAssignMode] = useState("all");
  const [assignSel, setAssignSel] = useState({});
  const [assignSearch, setAssignSearch] = useState("");
  const [savingAssign, setSavingAssign] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/exams/${id}`);
      setExam(data);
    } catch {
      toast.error("Examination not found");
      navigate("/admin/exams");
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const questions = exam?.questions || [];

  // questions already in the exam — can't be re-added from the bank until removed
  const addedSourceIds = new Set();
  questions.forEach((q) => {
    if (q.source_id) addedSourceIds.add(q.source_id);
    addedSourceIds.add(q.id);
  });

  // exam composition derived purely from the added questions
  const composition = questions.reduce((acc, q) => { acc[q.type] = (acc[q.type] || 0) + 1; return acc; }, {});

  const totalCount = spec.reduce((sum, s) => sum + (parseInt(s.count, 10) || 0), 0);

  const updateSpec = (i, k, v) => setSpec(spec.map((s, j) => (j === i ? { ...s, [k]: v } : s)));
  const addSpecRow = () => setSpec([...spec, { qtype: "mcq", difficulty: "Medium", count: "5" }]);
  const removeSpecRow = (i) => setSpec(spec.filter((_, j) => j !== i));

  const generate = async () => {
    setGenerating(true);
    setPreview([]);
    try {
      const cleanSpec = spec
        .map((s) => ({ qtype: s.qtype, difficulty: s.difficulty, count: parseInt(s.count, 10) || 0 }))
        .filter((s) => s.count > 0);
      const { data } = await api.post("/ai/generate-questions", {
        subject: exam.subject,
        topic: gen.topic,
        subtopic: gen.subtopic || null,
        spec: cleanSpec,
        default_marks: parseFloat(gen.default_marks) || 1,
        options_count: parseInt(gen.options_count, 10) || 4,
        save_to_bank: true,
      });
      setPreview(data.questions);
      toast.success(`Generated ${data.questions.length} questions (saved to bank)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const addPreviewToExam = async () => {
    const { data } = await api.post(`/exams/${id}/questions`, { questions: preview });
    toast.success(`Added ${data.added} question(s)` + (data.skipped_duplicates ? `, ${data.skipped_duplicates} duplicate(s) skipped` : ""));
    setPreview([]);
    load();
  };

  const deleteQuestion = async (qid) => {
    await api.delete(`/exams/${id}/questions/${qid}`);
    load();
  };

  const loadBank = async () => {
    const { data } = await api.get("/question-bank", { params: { subject: exam.subject } });
    setBank(data);
  };

  const deleteBankQuestion = async (qid) => {
    await api.delete(`/question-bank/${qid}`);
    toast.success("Removed from question bank");
    loadBank();
  };

  const addBankSelection = async () => {
    const chosen = bank.filter((q) => selectedBank[q.id] && !addedSourceIds.has(q.id));
    if (chosen.length === 0) return toast.error("Select questions that aren't already in the exam");
    const { data } = await api.post(`/exams/${id}/questions`, { questions: chosen });
    toast.success(`Added ${data.added} question(s)` + (data.skipped_duplicates ? `, ${data.skipped_duplicates} skipped` : ""));
    setSelectedBank({});
    load();
  };

  const openAssign = async () => {
    const { data } = await api.get("/admin/students", { params: { status: "approved", search: "" } });
    setStudents(data);
    setAssignMode(exam.assignment_mode === "selected" ? "selected" : "all");
    const sel = {};
    (exam.assigned_student_ids || []).forEach((sid) => { sel[sid] = true; });
    setAssignSel(sel);
    setAssignSearch("");
    setAssignOpen(true);
  };

  const saveAssign = async () => {
    setSavingAssign(true);
    try {
      const ids = Object.keys(assignSel).filter((k) => assignSel[k]);
      if (assignMode === "selected" && ids.length === 0) {
        toast.error("Select at least one student or choose 'All approved students'");
        setSavingAssign(false);
        return;
      }
      const { data } = await api.post(`/exams/${id}/assign`, { mode: assignMode, student_ids: ids });
      setExam(data);
      toast.success("Audience updated");
      setAssignOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update audience");
    } finally {
      setSavingAssign(false);
    }
  };

  const publish = async () => {
    try {
      await api.post(`/exams/${id}/publish`);
      toast.success("Examination published");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Publish failed");
    }
  };

  if (!exam) return <DashboardLayout nav={adminNav} title="Examination"><div className="py-20 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-primary" /></div></DashboardLayout>;

  return (
    <DashboardLayout nav={adminNav} title="Build Examination">
      <button onClick={() => navigate("/admin/exams")} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4" data-testid="back-to-exams">
        <ArrowLeft className="h-4 w-4" /> Back to examinations
      </button>

      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <StatusBadge status={exam.status} />
          <h1 className="font-display text-2xl md:text-3xl font-bold tracking-tight mt-2">{exam.title}</h1>
          <p className="text-muted-foreground">{exam.subject} · {questions.length} questions · {exam.total_marks} marks · {exam.duration_minutes} min</p>
          {questions.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3" data-testid="exam-composition">
              {Object.entries(composition).map(([t, c]) => (
                <span key={t} className="text-xs font-medium px-2 py-1 bg-secondary border border-border">{c} × {TYPE_LABEL[t] || t}</span>
              ))}
            </div>
          )}
          <div className="mt-3 flex items-center gap-1.5 text-xs text-muted-foreground" data-testid="audience-summary">
            <Users className="h-3.5 w-3.5" />
            Audience: {exam.assignment_mode === "selected"
              ? `${(exam.assigned_student_ids || []).length} selected student(s)`
              : "All approved students"}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" className="rounded-sm" onClick={openAssign} data-testid="assign-btn">
            <Users className="h-4 w-4 mr-1.5" /> Audience
          </Button>
          <Button onClick={publish} className="rounded-sm" disabled={exam.status === "published"} data-testid="publish-exam-btn">
            <Send className="h-4 w-4 mr-1.5" /> {exam.status === "published" ? "Published" : "Publish"}
          </Button>
        </div>
      </div>

      <Tabs defaultValue="generate">
        <TabsList>
          <TabsTrigger value="generate" data-testid="tab-generate"><Wand2 className="h-4 w-4 mr-1.5" /> AI Generator</TabsTrigger>
          <TabsTrigger value="bank" data-testid="tab-bank" onClick={loadBank}><Database className="h-4 w-4 mr-1.5" /> Question Bank</TabsTrigger>
          <TabsTrigger value="questions" data-testid="tab-questions">Questions ({questions.length})</TabsTrigger>
        </TabsList>

        {/* AI Generator */}
        <TabsContent value="generate" className="mt-4">
          <div className="grid lg:grid-cols-2 gap-6">
            <div className="bg-card border border-border p-6 space-y-4">
              <h3 className="font-semibold flex items-center gap-2"><Sparkles className="h-4 w-4 text-primary" /> Generate with AI</h3>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Subject</Label><Input value={exam.subject} disabled /></div>
                <div className="space-y-1.5"><Label>Topic</Label><Input value={gen.topic} onChange={(e) => setGen({ ...gen, topic: e.target.value })} placeholder="e.g. Linked Lists" data-testid="gen-topic" /></div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1.5"><Label>Sub-topic</Label><Input value={gen.subtopic} onChange={(e) => setGen({ ...gen, subtopic: e.target.value })} placeholder="Optional" /></div>
                <div className="space-y-1.5"><Label>Options</Label><Input type="text" inputMode="numeric" value={gen.options_count} onFocus={(e) => e.target.select()} onChange={(e) => setGen({ ...gen, options_count: (e.target.value.replace(/[^0-9]/g, "") || "") })} /></div>
                <div className="space-y-1.5"><Label>Marks/Q</Label><Input type="text" inputMode="decimal" value={gen.default_marks} onFocus={(e) => e.target.select()} onChange={(e) => setGen({ ...gen, default_marks: e.target.value.replace(/[^0-9.]/g, "") })} /></div>
              </div>

              <div className="space-y-2">
                <Label>Question distribution</Label>
                {spec.map((s, i) => (
                  <div key={i} className="flex gap-2 items-center" data-testid={`spec-row-${i}`}>
                    <Select value={s.qtype} onValueChange={(v) => updateSpec(i, "qtype", v)}>
                      <SelectTrigger className="flex-1"><SelectValue /></SelectTrigger>
                      <SelectContent>{QTYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
                    </Select>
                    <Select value={s.difficulty} onValueChange={(v) => updateSpec(i, "difficulty", v)}>
                      <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
                      <SelectContent>{DIFFS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                    </Select>
                    <Input
                      type="text"
                      inputMode="numeric"
                      className="w-20 text-center"
                      placeholder="Qty"
                      value={s.count}
                      onFocus={(e) => e.target.select()}
                      onChange={(e) => updateSpec(i, "count", e.target.value.replace(/[^0-9]/g, ""))}
                      data-testid={`spec-count-${i}`}
                    />
                    {spec.length > 1 && <Button size="icon" variant="ghost" className="text-destructive" onClick={() => removeSpecRow(i)}><X className="h-4 w-4" /></Button>}
                  </div>
                ))}
                <div className="flex items-center justify-between">
                  <Button size="sm" variant="outline" className="rounded-sm" onClick={addSpecRow} data-testid="add-spec-row"><Plus className="h-4 w-4 mr-1" /> Add row</Button>
                  <span className="text-xs text-muted-foreground font-mono">Total: {totalCount}</span>
                </div>
              </div>

              <p className="text-xs text-muted-foreground">Generated questions are automatically saved to the Question Bank.</p>

              <Button onClick={generate} disabled={generating || !gen.topic.trim() || totalCount <= 0} className="w-full rounded-sm" data-testid="generate-btn">
                {generating ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Generating…</> : <><Wand2 className="h-4 w-4 mr-2" /> Generate Questions</>}
              </Button>
            </div>

            <div className="bg-card border border-border p-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold">Preview ({preview.length})</h3>
                {preview.length > 0 && <Button size="sm" className="rounded-sm" onClick={addPreviewToExam} data-testid="add-preview-btn"><Plus className="h-4 w-4 mr-1" /> Add to exam</Button>}
              </div>
              <div className="space-y-3 max-h-[520px] overflow-y-auto">
                {preview.length === 0 && <div className="text-sm text-muted-foreground py-10 text-center">Generated questions will appear here.</div>}
                {preview.map((q, i) => <QuestionCard key={q.id} q={q} idx={i} />)}
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Question Bank */}
        <TabsContent value="bank" className="mt-4">
          <div className="bg-card border border-border p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold">Import from Question Bank</h3>
              <Button size="sm" className="rounded-sm" onClick={addBankSelection} data-testid="add-bank-btn">Add selected</Button>
            </div>
            <div className="space-y-3 max-h-[560px] overflow-y-auto">
              {bank.length === 0 && <div className="text-sm text-muted-foreground py-10 text-center">No bank questions for this subject yet. Generate some in the AI Generator tab.</div>}
              {bank.map((q, i) => {
                const added = addedSourceIds.has(q.id);
                return (
                  <div key={q.id} className="flex gap-3 items-start" data-testid={`bank-item-${q.id}`}>
                    <input
                      type="checkbox"
                      className="mt-4 accent-primary disabled:opacity-40"
                      disabled={added}
                      checked={!!selectedBank[q.id]}
                      onChange={(e) => setSelectedBank({ ...selectedBank, [q.id]: e.target.checked })}
                      data-testid={`bank-check-${q.id}`}
                    />
                    <div className="flex-1"><QuestionCard q={q} idx={i} added={added} onDelete={deleteBankQuestion} /></div>
                  </div>
                );
              })}
            </div>
          </div>
        </TabsContent>

        {/* Questions */}
        <TabsContent value="questions" className="mt-4">
          <div className="space-y-3">
            {questions.length === 0 && <div className="border border-dashed border-border p-12 text-center text-muted-foreground">No questions yet. Use the AI Generator or Question Bank.</div>}
            {questions.map((q, i) => <QuestionCard key={q.id} q={q} idx={i} onDelete={deleteQuestion} />)}
          </div>
        </TabsContent>
      </Tabs>

      {/* Audience selector */}
      <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Exam audience</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Choose who can see and take this exam.</p>
          <div className="flex gap-2">
            <Button variant={assignMode === "all" ? "default" : "outline"} className="flex-1 rounded-sm" onClick={() => setAssignMode("all")} data-testid="audience-all">
              All approved students
            </Button>
            <Button variant={assignMode === "selected" ? "default" : "outline"} className="flex-1 rounded-sm" onClick={() => setAssignMode("selected")} data-testid="audience-selected">
              Selected students
            </Button>
          </div>

          {assignMode === "selected" && (
            <div className="space-y-2">
              <Input placeholder="Search students…" value={assignSearch} onChange={(e) => setAssignSearch(e.target.value)} data-testid="audience-search" />
              <div className="max-h-72 overflow-y-auto border border-border divide-y divide-border">
                {students.length === 0 && <div className="p-4 text-sm text-muted-foreground text-center">No approved students yet.</div>}
                {students
                  .filter((s) => `${s.name} ${s.email} ${s.registration_number} ${s.roll_number}`.toLowerCase().includes(assignSearch.toLowerCase()))
                  .map((s) => (
                    <label key={s.id} className="flex items-center gap-3 p-3 cursor-pointer hover:bg-secondary/40" data-testid={`assign-student-${s.id}`}>
                      <input type="checkbox" className="accent-primary" checked={!!assignSel[s.id]} onChange={(e) => setAssignSel({ ...assignSel, [s.id]: e.target.checked })} />
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate">{s.name}</div>
                        <div className="text-xs text-muted-foreground font-mono">{s.registration_number} · {s.roll_number}</div>
                      </div>
                    </label>
                  ))}
              </div>
              <div className="text-xs text-muted-foreground">{Object.values(assignSel).filter(Boolean).length} selected</div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignOpen(false)}>Cancel</Button>
            <Button onClick={saveAssign} disabled={savingAssign} className="rounded-sm" data-testid="save-audience">
              {savingAssign && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Save audience
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
